import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Search } from 'lucide-react';
import { PageShell } from '@/components/brand/PageShell';
import { BrandButton } from '@/components/brand/Buttons';
import { LogoMark } from '@/components/brand/Logo';

/**
 * NotFound — a real 404 instead of the old catch-all redirect to home.
 *
 * Why this matters: silently redirecting unknown URLs to the homepage
 * (a) confuses users who mistyped or followed a dead link — the page
 * changes under them with no explanation, and (b) tells crawlers every
 * URL on the domain resolves 200 → soft-404 penalties in Search Console.
 */
const NotFound = () => {
  const navigate = useNavigate();

  return (
    <PageShell>
      <div className="container-page py-24 sm:py-36 text-center">
        <LogoMark size={34} className="text-clay-700 mx-auto mb-7" />
        <div className="eyebrow mb-4">404 — not found</div>
        <h1 className="font-display text-display-xl text-ink leading-[1.05] max-w-xl mx-auto">
          This page seems to have
          <br />
          <span className="italic text-clay-700" style={{ fontVariationSettings: '"opsz" 144, "SOFT" 80' }}>
            wandered off.
          </span>
        </h1>
        <p className="mt-6 text-lg text-ink-soft max-w-md mx-auto leading-relaxed">
          The link may be old, or the registry it pointed to may have been removed.
        </p>
        <div className="mt-9 flex flex-col sm:flex-row gap-3 justify-center">
          <BrandButton onClick={() => navigate('/')}>
            Back to home
            <ArrowRight className="h-4 w-4" />
          </BrandButton>
          <BrandButton variant="outline" onClick={() => navigate('/search-registry')}>
            <Search className="h-4 w-4" />
            Find a registry
          </BrandButton>
        </div>
      </div>
    </PageShell>
  );
};

export default NotFound;
